// frontend-dash/src/utils/colorGenerator.js
// Material You Color Generator — self-contained, no external dependencies.
// Generates a full MD3 color scheme from a seed color using HSL-based tonal palettes.

/**
 * Hex to RGB
 */
function hexToRgb(hex) {
  const c = hex.replace('#', '');
  return [parseInt(c.substring(0, 2), 16), parseInt(c.substring(2, 4), 16), parseInt(c.substring(4, 6), 16)];
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => Math.round(Math.max(0, Math.min(255, x))).toString(16).padStart(2, '0')).join('');
}

/**
 * RGB ↔ HSL conversions
 */
function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h, s, l) {
  h /= 360; s /= 100; l /= 100;
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

function hslToHex(h, s, l) {
  const [r, g, b] = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

/**
 * Generate a tonal palette (13 tones) from a hue and chroma (saturation).
 * Tones: 0, 5, 10, 15, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 95, 98, 99, 100
 */
function generateTonalPalette(hue, saturation) {
  const tones = [0, 5, 10, 15, 20, 25, 30, 35, 40, 50, 60, 70, 80, 90, 95, 98, 99, 100];
  const palette = {};
  for (const tone of tones) {
    // Scale saturation — less saturated at extremes
    const satScale = tone <= 10 || tone >= 95 ? 0.3 : tone <= 20 || tone >= 90 ? 0.6 : tone <= 30 || tone >= 80 ? 0.8 : 1.0;
    palette[tone] = hslToHex(hue, saturation * satScale, tone);
  }
  return palette;
}

/**
 * Generate a complete Material You theme from a seed color hex string.
 */
export function generateThemeFromSeed(seedHex) {
  const [r, g, b] = hexToRgb(seedHex);
  const [h, s] = rgbToHsl(r, g, b);

  // Derive hues for secondary and tertiary (Material You style)
  const primaryHue = h;
  const secondaryHue = h; // Same hue, lower saturation
  const tertiaryHue = (h + 60) % 360; // Complementary-ish
  const neutralHue = h;
  const neutralVariantHue = h;
  const errorHue = 25; // Fixed reddish

  const primarySat = Math.min(s, 65);
  const secondarySat = Math.max(primarySat * 0.4, 10);
  const tertiarySat = Math.min(primarySat * 0.7, 55);
  const neutralSat = Math.max(primarySat * 0.08, 4);
  const neutralVariantSat = Math.max(primarySat * 0.15, 6);
  const errorSat = 80;

  // Generate tonal palettes
  const palettes = {
    primary: generateTonalPalette(primaryHue, primarySat),
    secondary: generateTonalPalette(secondaryHue, secondarySat),
    tertiary: generateTonalPalette(tertiaryHue, tertiarySat),
    neutral: generateTonalPalette(neutralHue, neutralSat),
    neutralVariant: generateTonalPalette(neutralVariantHue, neutralVariantSat),
    error: generateTonalPalette(errorHue, errorSat),
  };

  // Map palettes to Light scheme
  const light = {
    primary: palettes.primary[40],
    onPrimary: palettes.primary[100],
    primaryContainer: palettes.primary[90],
    onPrimaryContainer: palettes.primary[30],
    secondary: palettes.secondary[40],
    onSecondary: palettes.secondary[100],
    secondaryContainer: palettes.secondary[90],
    onSecondaryContainer: palettes.secondary[30],
    tertiary: palettes.tertiary[40],
    onTertiary: palettes.tertiary[100],
    tertiaryContainer: palettes.tertiary[90],
    onTertiaryContainer: palettes.tertiary[30],
    error: palettes.error[40],
    onError: palettes.error[100],
    errorContainer: palettes.error[90],
    onErrorContainer: palettes.error[10],
    surface: palettes.neutral[99],
    onSurface: palettes.neutral[10],
    surfaceVariant: palettes.neutralVariant[90],
    onSurfaceVariant: palettes.neutralVariant[30],
    surfaceDim: palettes.neutral[87] || palettes.neutral[90],
    surfaceBright: palettes.neutral[98],
    surfaceContainerLowest: palettes.neutral[100],
    surfaceContainerLow: palettes.neutral[96] || palettes.neutral[95],
    surfaceContainer: palettes.neutral[94] || palettes.neutral[95],
    surfaceContainerHigh: palettes.neutral[92] || palettes.neutral[90],
    surfaceContainerHighest: palettes.neutral[90],
    outline: palettes.neutralVariant[50],
    outlineVariant: palettes.neutralVariant[80],
    inverseSurface: palettes.neutral[20],
    inverseOnSurface: palettes.neutral[95],
    inversePrimary: palettes.primary[80],
    scrim: '#000000',
  };

  // Map palettes to Dark scheme
  const dark = {
    primary: palettes.primary[80],
    onPrimary: palettes.primary[20],
    primaryContainer: palettes.primary[30],
    onPrimaryContainer: palettes.primary[90],
    secondary: palettes.secondary[80],
    onSecondary: palettes.secondary[20],
    secondaryContainer: palettes.secondary[30],
    onSecondaryContainer: palettes.secondary[90],
    tertiary: palettes.tertiary[80],
    onTertiary: palettes.tertiary[20],
    tertiaryContainer: palettes.tertiary[30],
    onTertiaryContainer: palettes.tertiary[90],
    error: palettes.error[80],
    onError: palettes.error[20],
    errorContainer: palettes.error[30],
    onErrorContainer: palettes.error[90],
    surface: palettes.neutral[10],
    onSurface: palettes.neutral[90],
    surfaceVariant: palettes.neutralVariant[30],
    onSurfaceVariant: palettes.neutralVariant[80],
    surfaceDim: palettes.neutral[10],
    surfaceBright: palettes.neutral[25] || palettes.neutral[30],
    surfaceContainerLowest: palettes.neutral[5] || palettes.neutral[0],
    surfaceContainerLow: palettes.neutral[10],
    surfaceContainer: palettes.neutral[15] || palettes.neutral[10],
    surfaceContainerHigh: palettes.neutral[20],
    surfaceContainerHighest: palettes.neutral[25] || palettes.neutral[30],
    outline: palettes.neutralVariant[60],
    outlineVariant: palettes.neutralVariant[30],
    inverseSurface: palettes.neutral[90],
    inverseOnSurface: palettes.neutral[20],
    inversePrimary: palettes.primary[40],
    scrim: '#000000',
  };

  return { seed: seedHex, light, dark, palettes };
}

/**
 * Convert hex to "R G B" for CSS custom properties.
 */
function hexToRgbString(hex) {
  const [r, g, b] = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}

/**
 * Map a scheme to CSS custom property values.
 */
export function schemeToCssVars(scheme) {
  const mapping = {
    '--color-primary': scheme.primary,
    '--color-primary-on': scheme.onPrimary,
    '--color-primary-container': scheme.primaryContainer,
    '--color-primary-on-container': scheme.onPrimaryContainer,
    '--color-secondary': scheme.secondary,
    '--color-secondary-on': scheme.onSecondary,
    '--color-secondary-container': scheme.secondaryContainer,
    '--color-secondary-on-container': scheme.onSecondaryContainer,
    '--color-tertiary': scheme.tertiary,
    '--color-tertiary-on': scheme.onTertiary,
    '--color-tertiary-container': scheme.tertiaryContainer,
    '--color-tertiary-on-container': scheme.onTertiaryContainer,
    '--color-error': scheme.error,
    '--color-error-on': scheme.onError,
    '--color-error-container': scheme.errorContainer,
    '--color-error-on-container': scheme.onErrorContainer,
    '--color-surface': scheme.surface,
    '--color-surface-on': scheme.onSurface,
    '--color-surface-dim': scheme.surfaceDim,
    '--color-surface-bright': scheme.surfaceBright,
    '--color-surface-container-lowest': scheme.surfaceContainerLowest,
    '--color-surface-container-low': scheme.surfaceContainerLow,
    '--color-surface-container': scheme.surfaceContainer,
    '--color-surface-container-high': scheme.surfaceContainerHigh,
    '--color-surface-container-highest': scheme.surfaceContainerHighest,
    '--color-surface-variant': scheme.surfaceVariant,
    '--color-surface-on-variant': scheme.onSurfaceVariant,
    '--color-outline': scheme.outline,
    '--color-outline-variant': scheme.outlineVariant,
    '--color-inverse-surface': scheme.inverseSurface,
    '--color-inverse-on-surface': scheme.inverseOnSurface,
    '--color-inverse-primary': scheme.inversePrimary,
    '--color-scrim': scheme.scrim,
  };
  const cssVars = {};
  for (const [k, v] of Object.entries(mapping)) {
    if (v) cssVars[k] = hexToRgbString(v);
  }
  return cssVars;
}

export function applyCssVars(cssVars) {
  for (const [k, v] of Object.entries(cssVars)) {
    document.documentElement.style.setProperty(k, v);
  }
}

export function clearCssVars() {
  const vars = Object.keys(schemeToCssVars({
    primary:'#000',onPrimary:'#000',primaryContainer:'#000',onPrimaryContainer:'#000',
    secondary:'#000',onSecondary:'#000',secondaryContainer:'#000',onSecondaryContainer:'#000',
    tertiary:'#000',onTertiary:'#000',tertiaryContainer:'#000',onTertiaryContainer:'#000',
    error:'#000',onError:'#000',errorContainer:'#000',onErrorContainer:'#000',
    surface:'#000',onSurface:'#000',surfaceDim:'#000',surfaceBright:'#000',
    surfaceContainerLowest:'#000',surfaceContainerLow:'#000',surfaceContainer:'#000',
    surfaceContainerHigh:'#000',surfaceContainerHighest:'#000',
    surfaceVariant:'#000',onSurfaceVariant:'#000',
    outline:'#000',outlineVariant:'#000',
    inverseSurface:'#000',inverseOnSurface:'#000',inversePrimary:'#000',scrim:'#000',
  }));
  for (const v of vars) document.documentElement.style.removeProperty(v);
}

export function serializeTheme(theme) {
  return { seed: theme.seed, light: theme.light, dark: theme.dark };
}

export function applyTheme(theme) {
  if (!theme || !theme.light || !theme.dark) return;

  const lightVars = schemeToCssVars(theme.light);
  const darkVars = schemeToCssVars(theme.dark);

  let styleEl = document.getElementById('md3-dynamic-theme');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'md3-dynamic-theme';
    document.head.appendChild(styleEl);
  }

  const lightCss = Object.entries(lightVars).map(([k, v]) => `${k}: ${v};`).join('\n  ');
  const darkCss = Object.entries(darkVars).map(([k, v]) => `${k}: ${v};`).join('\n  ');

  styleEl.innerHTML = `
:root {
  ${lightCss}
}
.dark {
  ${darkCss}
}
@media (prefers-color-scheme: dark) {
  :root:not(.light) {
    ${darkCss}
  }
}
  `;
}
